import React, { FC, useEffect, useState, useRef } from 'react';
import { Form, Formik, FormikHelpers, useField } from 'formik';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import useLocales from 'hooks/useLocales';
import { IJobForm } from 'core/interface/job';
import { InputType } from 'core/enum/inputType';
import { degreeTypes, statusTypes } from 'core/constants/aboutMePopUpConstants';
import {
  freelanceSalary,
  monthlySalary,
  workExperience,
  hourlyRateChoices
} from 'core/constants/preferenceConstats';
import { JobService } from 'services/jobs/job';
import { ProfileService } from 'services/profile/profile';
import { SkillService } from 'services/skills/skillService';
import FormField from 'components/FormField/Index';
import Checkboxes from 'components/shared/Checkbox/Index';
import DropDown from 'components/Dropdown';
import Autocomplete from 'components/shared/Autocomplete/Index';
import Button from 'components/Button/Index';
import Image from 'components/image/Image';
import { initialValues, validation } from './form';
import Multiselect from 'components/shared/Multiselect/Index';
import { OptionType } from 'core/interface/autocomplete';
import WritableChip from 'components/shared/WritableChip/Index';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import SingleSelectFormik from 'components/shared/SelectSingleFormik';
import { jobContracts } from 'core/constants/workExperience';
import { useDebounce } from 'helpers/useDebounce';

import './Index.scss';
import { IMe } from 'core/interface/me';

const NewJobPopup: FC<any> = ({ nextStep, selectedJob }): JSX.Element => {
  const { t } = useLocales();
  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );
  const { cities } = useSelector((state: RootState) => state.locations);
  const [selectedIndustries, setSelectedIndustries] = useState<OptionType[]>(
    []
  );
  const [selectedLocations, setSelectedLocations] = useState<OptionType[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [locationQuery, setLocationQuery] = useState<string>('');
  const debouncedLocationQuery = useDebounce(locationQuery, 500);
  const [errorsForFields, setErrors] = useState({
    industriesError: false,
    requirementsError: false,
    skillsError: false,
    locationsError: false
  });
  const { computerSkills } = useSelector(
    (state: RootState) => state.computerSkills
  );

  const me = useSelector((state: IMe) => state.user).user;

  const isAdmin = me?.role === 'admin';

  const getCities = async (q: string = '', limit: number = 10) => {
    JobService.getLocations({
      limit,
      q
    });
  };
  const { matchSearch } = useSelector((state: RootState) => state.match);

  const getSkills = (query: string = ''): void => {
    SkillService.getITSkillsList(query);
  };

  const getSearchProfile = (query: string = '') => {
    MatchProfileService.getMatchProfilesSearch(query);
  };

  useEffect(() => {
    getSearchProfile();
    getSkills();
    getIndustries();
    getCities();
  }, []);

  const getIndustries = (query: string = ''): void => {
    ProfileService.getIndustries(query);
  };

  const handleSubmit = (
    values: IJobForm,
    action: FormikHelpers<IJobForm>
  ): void => {
    // Format locations properly
    const formattedLocations = selectedLocations.map((location) => ({
      id: location.id,
      display_name: location.title || location.name
    }));

    const obj = {
      ...values,
      locations: formattedLocations,
      industries: getIndustriesId(),
      skills: getSkillsId(),
      edit: selectedJob?.id
    };
    nextStep(obj as any | number[]);
    action.resetForm();
  };

  const getIndustriesId = (): number[] => {
    const selectedIndustry: number[] = [];
    industries?.forEach((ind) => {
      selectedIndustries.forEach((valInd) => {
        if (valInd.id === ind.id) {
          selectedIndustry.push(ind.id);
        }
      });
    });
    return selectedIndustry;
  };

  const getSkillsId = (): number[] => {
    const skill: number[] = [];
    computerSkills?.forEach((ind) => {
      skills.forEach((valInd) => {
        if (valInd.id === ind.id) {
          skill.push(ind.id);
        }
      });
    });
    return skill;
  };

  const handleIndustryChange = (industry: OptionType[]): void => {
    setSelectedIndustries((prev) => {
      const newIndustries = industry.filter((newIndustry) => {
        return !prev.some(
          (existingIndustry) => existingIndustry.id === newIndustry.id
        );
      });
      return [...prev, ...newIndustries];
    });
    if (!selectedIndustries.length) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        industriesError: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        industriesError: false
      }));
    }
  };

  const handleLocationChange = (locations: OptionType[]): void => {
    setSelectedLocations((prev) => {
      const newLocations = locations.filter((newLocation) => {
        return !prev.some(
          (existingLocation) => existingLocation.id === newLocation.id
        );
      });
      return [...prev, ...newLocations];
    });
    if (!selectedLocations.length) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        locationsError: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        locationsError: false
      }));
    }
  };

  const handleRemoveIndustry = (e: any): void => {
    setSelectedIndustries((prev) =>
      prev.filter((ind) => (ind.title || ind.name || ind) !== e)
    );
  };

  const handleSkillChange = (selectedSkills: OptionType[]): void => {
    setSkills((prevSkills) => {
      if (typeof selectedSkills === 'string') {
        // Check if string already exists in skills array
        const skillExists = prevSkills.some((existingSkill) => {
          if (typeof existingSkill === 'string') {
            return (
              existingSkill.toLowerCase() ===
              (selectedSkills as string).toLowerCase()
            );
          }
          const existingTitle = existingSkill.title as string | undefined;
          const existingName = existingSkill.name as string | undefined;
          return (
            (existingTitle &&
              existingTitle.toLowerCase() ===
                (selectedSkills as string).toLowerCase()) ||
            (existingName &&
              existingName.toLowerCase() ===
                (selectedSkills as string).toLowerCase())
          );
        });

        return skillExists ? prevSkills : [...prevSkills, selectedSkills];
      }

      // Process all skills in the array (both existing and new free solo inputs)
      const newSkills: Array<OptionType | string> = [];

      for (const skill of selectedSkills) {
        // Check if this is a free solo input (string) or an object
        if (typeof skill === 'string') {
          // Check if string already exists in skills array or newSkills
          const skillExists =
            prevSkills.some((existingSkill) => {
              if (typeof existingSkill === 'string') {
                return (
                  existingSkill.toLowerCase() ===
                  (skill as string).toLowerCase()
                );
              }
              const existingTitle = existingSkill.title as string | undefined;
              const existingName = existingSkill.name as string | undefined;
              return (
                (existingTitle &&
                  existingTitle.toLowerCase() ===
                    (skill as string).toLowerCase()) ||
                (existingName &&
                  existingName.toLowerCase() ===
                    (skill as string).toLowerCase())
              );
            }) ||
            newSkills.some(
              (newSkill) =>
                typeof newSkill === 'string' &&
                (newSkill as string).toLowerCase() ===
                  (skill as string).toLowerCase()
            );

          if (!skillExists) {
            newSkills.push(skill);
          }
        } else if (
          !prevSkills.some(
            (existingSkill) =>
              typeof existingSkill !== 'string' && existingSkill.id === skill.id
          )
        ) {
          newSkills.push(skill);
        }
      }

      return [...prevSkills, ...newSkills];
    });

    if (!skills.length) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        skillsError: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        skillsError: false
      }));
    }
  };
  const handleRemoveSkill = (skillToRemove: any): void => {
    setSkills((prev) => {
      if (typeof skillToRemove === 'string') {
        return prev.filter((skill) => {
          // Handle both object skills and string skills
          const skillName =
            typeof skill === 'string' ? skill : skill.title || skill.name || '';
          return skillName !== skillToRemove;
        });
      } else if (skillToRemove.id) {
        return prev.filter((skill) => {
          if (typeof skill === 'string') return true;
          return skill.id !== skillToRemove.id;
        });
      }
      return prev;
    });
  };

  const filterItems = (data: any, filterData: any) => {
    const selectedArray: any = [];

    filterData?.forEach((id: any) => {
      const item = data.find((item: any) => item.id === id);
      if (item) {
        selectedArray.push(item);
      }
    });

    return selectedArray;
  };

  // Function to fetch specific location IDs for a job
  const fetchLocationsByIds = async (locationIds: (string | number)[]) => {
    if (!locationIds || locationIds.length === 0) return [];

    // First try to find the locations in the already loaded cities
    const missingLocationIds = locationIds.filter(
      (id) => !cities?.some((city: any) => String(city.id) === String(id))
    );

    if (missingLocationIds.length === 0) return [];

    // Fetch each missing location individually by ID
    const fetchPromises = missingLocationIds.map((id) =>
      JobService.getLocationById(id)
    );

    try {
      // Wait for all location fetch operations to complete
      const results = await Promise.all(fetchPromises);
      return results.filter(Boolean);
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  };

  // Helper to extract location IDs from selectedJob
  const getLocationIdsFromJob = () => {
    if (!selectedJob?.locations) return [];

    return selectedJob.locations
      .map((loc: any) => {
        if (typeof loc === 'object' && loc !== null && loc.id) {
          return loc.id;
        }
        return loc;
      })
      .filter(Boolean);
  };

  // Helper to format a location object consistently
  const formatLocationObject = (loc: any) => {
    return {
      id: loc.id,
      title: loc.display_name,
      name: loc.display_name
    };
  };

  const formatLocationsFromSelectedJob = async () => {
    if (!selectedJob?.locations) return [];

    // Get all location IDs from the selected job
    const locationIds = getLocationIdsFromJob();

    // Fetch any missing locations
    const fetchedLocations = await fetchLocationsByIds(locationIds);

    const formattedLocations = selectedJob.locations
      .map((loc: any) => {
        // Handle if location is already an object with id and display_name
        if (
          typeof loc === 'object' &&
          loc !== null &&
          loc.id &&
          loc.display_name
        ) {
          return formatLocationObject(loc);
        }

        // Handle if location is just an ID (number or string)
        const locationId = typeof loc === 'object' ? loc.id : loc;
        const locationObj = cities?.find(
          (city: any) => String(city?.id) === String(locationId)
        );

        if (locationObj) {
          return formatLocationObject(locationObj);
        }

        // Check fetchedLocations as fallback
        const fetchedLocation = fetchedLocations?.find(
          (location: any) => String(location?.id) === String(locationId)
        );

        if (fetchedLocation) {
          return formatLocationObject(fetchedLocation);
        }

        return null;
      })
      .filter(Boolean);

    return formattedLocations;
  };

  useEffect(() => {
    if (
      selectedJob?.locations?.length > 0 &&
      (!cities || cities.length === 0)
    ) {
      getCities();
    }
  }, [selectedJob]);

  const initializedRef = useRef(false);

  useEffect(() => {
    initializedRef.current = false;
  }, [selectedJob?.id]);

  useEffect(() => {
    if (selectedJob && selectedJob.id && !initializedRef.current) {
      setSelectedIndustries(filterItems(industries, selectedJob?.industries));
      setSkills(filterItems(computerSkills, selectedJob?.skills));

      // Handle the async formatLocationsFromSelectedJob function
      formatLocationsFromSelectedJob().then((locations) => {
        setSelectedLocations(locations);
        initializedRef.current = true;
      });
    }
  }, [selectedJob, cities, industries, computerSkills]);

  const initialValuesForEdit: any = {
    skills,
    degree: selectedJob?.degree ?? 0,
    industries: selectedIndustries,
    is_public: selectedJob?.is_public ?? false,
    job_description: selectedJob?.description ?? '',
    job_title: selectedJob?.title ?? '',
    locations: selectedLocations,
    required_experience: selectedJob?.work_exp ?? 0, // API field: work_exp -> Form field: required_experience
    requirements: selectedJob?.musts ?? [], // API field: musts -> Form field: requirements
    culture_profile: selectedJob?.culture_profile ?? null,
    is_remote: selectedJob?.is_remote ?? false,
    if_office_work: selectedJob?.if_office_work ?? false,
    status: selectedJob?.status ?? '',
    contract_type: selectedJob?.contract ?? 0,
    compensation: selectedJob?.compensation ?? 0
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  useEffect(() => {
    if (debouncedLocationQuery) {
      getCities(debouncedLocationQuery);
    } else {
      getCities();
    }
  }, [debouncedLocationQuery]);

  return (
    <div className="njob scrollable">
      <Formik
        initialValues={selectedJob?.id ? initialValuesForEdit : initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
        validateOnMount
        validationSchema={validation(t)}
      >
        {({
          setFieldValue,
          setFieldError,
          setFieldTouched,
          isValid,
          errors,
          touched,
          values
        }) => {
          return (
            <Form>
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Job title')}</span>
                  <FormField
                    fieldName="job_title"
                    placeholder={t('Job title')}
                    type={InputType.text}
                    inputClassName="input"
                  />
                </div>
                <div className="field many autocomplete">
                  <span className="label">{t('Select culture profile')}</span>
                  <SingleSelectFormik
                    fieldName="culture_profile"
                    onChange={(e: any) => {
                      if (typeof e != 'string') {
                        setFieldValue('culture_profile', e);
                      }
                    }}
                    onBlur={(e: any) => {
                      if (
                        matchSearch?.length &&
                        !matchSearch.some(
                          (el: any) => el.title === e.target.value
                        )
                      ) {
                        setFieldValue('culture_profile', null);
                        setFieldError('culture_profile', 'required');
                        setFieldTouched('culture_profile', true);
                      }
                    }}
                    options={matchSearch}
                    show_location={false}
                    placeholder={''}
                    onInputChange={(e: any) => getSearchProfile(e)}
                    value={values?.culture_profile || ''}
                  />
                </div>
              </div>
              <div className="field">
                <span className="label">{t('Job description')}</span>
                <FormField
                  fieldName="job_description"
                  placeholder={t('Job description')}
                  type={InputType.text}
                  inputClassName="input"
                />
              </div>
              {isAdmin && (
                <div className="field">
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('is_public', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: t('Show this job on my careerpage'),
                        isChecked: values?.is_public
                      }
                    ]}
                  />
                </div>
              )}
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Required Degree')}</span>
                  <DropDown
                    placeholder={t('Degree')}
                    items={degreeTypes}
                    selectedValue={(e) => setFieldValue('degree', e)}
                    defaultValue={initialValuesForEdit?.degree}
                  />
                </div>
                <div className="field many">
                  <span className="label">{t('Locations')}</span>
                  <Multiselect
                    fieldName="locations"
                    value={selectedLocations}
                    className={`${
                      errors.locations && errorsForFields.locationsError
                        ? 'error'
                        : ''
                    }`}
                    placeholder={t('Country, province, city')}
                    show_location={true}
                    onChange={(e: string | OptionType[]) => {
                      if (typeof e != 'string' && Array.isArray(e)) {
                        handleLocationChange(e);
                        setFieldValue(
                          'locations',
                          e.map((location) => ({
                            id: location.id,
                            display_name: location.title || location.name
                          }))
                        );
                      }
                    }}
                    onInputChange={(e: any) => {
                      // Handle both string and event object
                      const value =
                        typeof e === 'object' && e?.target ? e.target.value : e;
                      setLocationQuery(value || '');
                    }}
                    options={
                      cities?.map((e: any) => ({
                        id: e?.id,
                        title: e?.display_name,
                        name: e?.display_name
                      })) || []
                    }
                    loadMore={handleLoadMoreCities}
                  />
                  <div className="selected-skills">
                    {selectedLocations?.map((location, index) => (
                      <div key={location.id || index} className="skill">
                        <div className="skill-name">
                          {location.name ?? location?.title}
                        </div>
                        <div
                          className="remove"
                          onClick={() => {
                            setSelectedLocations((prev) =>
                              prev.filter((loc) => loc.id !== location.id)
                            );
                            setFieldValue(
                              'locations',
                              selectedLocations
                                .filter((loc) => loc.id !== location.id)
                                .map((loc) => ({
                                  id: loc.id,
                                  display_name: loc.title || loc.name
                                }))
                            );
                          }}
                        >
                          <Image name="close.svg" width={10} height={10} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <span className="error-msg">
                    {errorsForFields.locationsError &&
                      touched.locations &&
                      errors.locations &&
                      t('At least one location is required')}
                  </span>
                </div>
              </div>
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Industries')}</span>
                  <Multiselect
                    fieldName="industries"
                    value={
                      initialValuesForEdit?.industries ?? selectedIndustries
                    }
                    className={`${
                      errors.industries && errorsForFields.industriesError
                        ? 'error'
                        : ''
                    }`}
                    placeholder={t('Industry')}
                    show_location={false}
                    onChange={(e: string | OptionType[]) => {
                      if (typeof e != 'string') {
                        handleIndustryChange(e);
                        setFieldValue('industries', [e[0].id]);
                      }
                    }}
                    onInputChange={(e) => getIndustries(e ?? '')}
                    options={
                      industries?.map((e) => ({
                        id: e?.id,
                        title: e?.name
                      })) || []
                    }
                  />
                  {selectedIndustries?.map((industry, index) => (
                    <span className="industry-selected" key={index}>
                      {industry.name ?? industry?.title}{' '}
                      <span
                        onClick={() => {
                          setFieldValue(
                            'industries',
                            selectedIndustries.filter(
                              (e) => e.id !== industry.id
                            )
                          );
                          handleRemoveIndustry(
                            industry.name ?? industry?.title ?? industry ?? ''
                          );
                        }}
                      >
                        <Image
                          className="remove"
                          name="removes.svg"
                          width={10}
                          height={10}
                        />
                      </span>
                    </span>
                  ))}
                  <span className="error-msg">
                    {errorsForFields.industriesError && errors.industries}
                  </span>
                </div>
                <div className="field many">
                  <span className="label">{t('Requirements')}</span>
                  <WritableChip
                    className={`${
                      errors.requirements &&
                      errorsForFields.requirementsError &&
                      touched.requirements
                        ? 'error'
                        : ''
                    }`}
                    inputClassName="input"
                    placeholder={t('')}
                    handleError={setErrors}
                    onSelectedValues={(e: string[]) =>
                      setFieldValue('requirements', e)
                    }
                    defaultValues={values?.requirements}
                    setTouch={() => setFieldTouched('requirements', true)}
                  />
                  <span className="error-msg">
                    {errorsForFields.requirementsError &&
                      touched.requirements &&
                      errors.requirements}
                  </span>
                </div>
              </div>
              <div className="checkboxes">
                <div className="field ">
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('is_remote', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: t('Remote work'),
                        isChecked: values?.is_remote
                      }
                    ]}
                  />
                </div>
                <div className="field many">
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('if_office_work', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: t('Office work'),
                        isChecked: values?.if_office_work
                      }
                    ]}
                  />
                </div>
              </div>
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Computer skills')}</span>
                  <Multiselect
                    fieldName="skills"
                    freeSolo
                    value={skills}
                    className={`${
                      errors.skills && errorsForFields.skillsError
                        ? 'error'
                        : ''
                    }`}
                    placeholder={t('Text')}
                    show_location={false}
                    onChange={(e: string | OptionType[]) => {
                      if (typeof e !== 'string' && Array.isArray(e)) {
                        // Get only the latest selection (last item in array)
                        const latestSelection =
                          e.length > 0 ? e[e.length - 1] : null;

                        if (latestSelection) {
                          // Pass just the new selection to handleSkillChange
                          handleSkillChange([latestSelection]);

                          // Update the formik field with all skills (existing + new)
                          const allSkills = [...skills];
                          if (typeof latestSelection === 'string') {
                            // Check if this string skill already exists
                            const skillExists = allSkills.some(
                              (existingSkill) =>
                                typeof existingSkill === 'string' &&
                                existingSkill.toLowerCase() ===
                                  (latestSelection as string).toLowerCase()
                            );
                            if (!skillExists) {
                              allSkills.push(latestSelection);
                            }
                          } else {
                            // Check if this object skill already exists
                            const skillExists = allSkills.some(
                              (existingSkill) =>
                                typeof existingSkill !== 'string' &&
                                existingSkill.id === latestSelection.id
                            );
                            if (!skillExists) {
                              allSkills.push(latestSelection);
                            }
                          }

                          // Update formik with the IDs for object skills and strings for string skills
                          const skillIds = allSkills.map((skill) => {
                            return typeof skill === 'string' ? skill : skill.id;
                          });
                          setFieldValue('skills', skillIds);
                        }
                      }
                    }}
                    onInputChange={(e) => getSkills(e ?? '')}
                    options={
                      computerSkills?.map((e) => ({
                        id: e?.id,
                        title: e?.name
                      })) || []
                    }
                  />
                  {skills?.map((skill, index) => (
                    <span className="industry-selected" key={index}>
                      {typeof skill === 'string'
                        ? skill
                        : skill.title ?? skill.name ?? skill}{' '}
                      <span
                        onClick={() => {
                          // Get current skills array without the removed skill
                          const updatedSkills = skills.filter((s) => {
                            if (
                              typeof skill === 'string' &&
                              typeof s === 'string'
                            ) {
                              return s !== skill;
                            } else if (
                              typeof skill !== 'string' &&
                              typeof s !== 'string'
                            ) {
                              return s.id !== skill.id;
                            }
                            return true;
                          });

                          // Update formik field value
                          const skillIds = updatedSkills.map((s) => {
                            return typeof s === 'string' ? s : s.id;
                          });
                          setFieldValue('skills', skillIds);

                          // Remove from skills state
                          handleRemoveSkill(skill);
                        }}
                      >
                        <Image
                          className="remove"
                          name="removes.svg"
                          width={10}
                          height={10}
                        />
                      </span>
                    </span>
                  ))}
                  <span className="error-msg">
                    {errorsForFields.skillsError && errors.skills}
                  </span>
                </div>
                <div className="field many">
                  <span className="label">{t('Status')}</span>
                  <DropDown
                    placeholder={t('Published')}
                    items={statusTypes}
                    selectedValue={(e) => setFieldValue('status', e)}
                    defaultValue={initialValuesForEdit?.status}
                  />
                </div>
              </div>
              <div className="fields">
                {/* <div className="field many autocomplete">
                  <span className="label">{t('Prefered monthly salary')}</span>
                  <DropDown
                    dropdownClassName={`${
                      errors.prefered_salary && touched.prefered_salary
                        ? 'error'
                        : ''
                    }`}
                    items={monthlySalary}
                    selectedValue={(e) => setFieldValue('prefered_salary', e)}
                    defaultValue={initialValuesForEdit?.prefered_salary}
                  />
                </div> */}
              </div>
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Required work experience')}</span>
                  <DropDown
                    items={workExperience}
                    selectedValue={(e) =>
                      setFieldValue('required_experience', e)
                    }
                    defaultValue={initialValuesForEdit?.required_experience}
                  />
                </div>
                <div className="field many">
                  <span className="label">{t('Contract')}</span>
                  <DropDown
                    items={jobContracts}
                    selectedValue={(e) => setFieldValue('contract_type', e)}
                    defaultValue={initialValuesForEdit?.contract_type}
                  />
                </div>
                <div className="field many autocomplete">
                  <span className="label">
                    {values?.contract_type === 2
                      ? t('Hourly Rate (EUR)')
                      : t('Salary Range(EUR)')}
                  </span>
                  <DropDown
                    dropdownClassName={`${
                      errors.compensation && touched.compensation ? 'error' : ''
                    }`}
                    items={
                      values?.contract_type === 2
                        ? hourlyRateChoices
                        : monthlySalary
                    }
                    selectedValue={(e) => setFieldValue('compensation', e)}
                    defaultValue={initialValuesForEdit?.compensation || ''}
                  />
                </div>
              </div>
              <Button
                t={t}
                type="submit"
                title="Next"
                className="save "
                disabled={!isValid && !selectedJob?.id}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NewJobPopup;
